// «Рынок → Аналитика»: цепочка стоимости по звеньям, сезонность и валютный
// эффект главного ряда, квоты вида и индекс рыбной корзины Росстата.
// Только открытые источники: опт РФ и сделки биржи — во внутренней справке.

import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { DASH, formatMonth, formatNumber, useLang, useT } from '../i18n'
import { linkKey, seasonRow, utilPct } from './analytics'
import { countryName, productKeys } from './customs'
import { usePricesUrl } from './pricesUrl'

export function AnalyticsPanel() {
  const t = useT()
  const { lang } = useLang()
  const sp = useQuery({ queryKey: ['cabinet', 'prices', 'analytics'],
    queryFn: () => cabinetApi.pricesAnalyticsSpecies(), retry: false })
  const list = sp.data?.species ?? []
  const url = usePricesUrl()
  const picked = url.get('analyticsSpecies')
  const code = (list.find((s) => s.code === picked) ?? list.find((s) => s.code === 'ALK') ?? list[0])?.code ?? null
  const setPicked = (c: string) => url.set({ analyticsSpecies: c })
  const ov = useQuery({ queryKey: ['cabinet', 'prices', 'analytics', code],
    queryFn: () => cabinetApi.pricesAnalytics(code!), enabled: !!code, retry: false })
  const basket = useQuery({ queryKey: ['cabinet', 'prices', 'basket'],
    queryFn: () => cabinetApi.pricesBasket(), retry: false })

  if (sp.isLoading) return <p className="text-sm text-mist">{t('prices.loading')}</p>
  if (sp.error) return <p className="text-sm text-mist">{t('prices.error')}</p>
  if (!list.length) return <p className="text-sm text-foam">{t('prices.analytics.empty')}</p>

  const d = ov.data
  const monthName = (m: number) =>
    new Intl.DateTimeFormat(lang, { month: 'short' }).format(new Date(2000, m - 1, 1))
  const lastIdx = basket.data?.index.slice(-1)[0]

  return (
    <div className="space-y-6 text-sm">
      <div className="flex flex-wrap gap-2">
        {list.map((s) => (
          <button key={s.code} type="button" onClick={() => setPicked(s.code)}
            className={`rounded-full border px-3 py-1 ${s.code === code
              ? 'border-accent text-foam' : 'border-line text-mist hover:text-foam'}`}>
            {lang === 'ru' ? s.name_ru : s.name_en}
          </button>
        ))}
      </div>
      {ov.isLoading && <p className="text-mist">{t('prices.loading')}</p>}
      {d && (
        <>
          <section>
            <h2 className="mb-2 text-foam">{t('prices.analytics.chain')}</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-xs">
                <thead>
                  <tr className="text-left text-mist">
                    <th className="py-1 pr-3 font-normal">{t('prices.analytics.link')}</th>
                    <th className="py-1 pr-3 font-normal">{t('prices.export.product')}</th>
                    <th className="py-1 pr-3 font-normal">{t('prices.analytics.where')}</th>
                    <th className="py-1 pr-3 text-right font-normal">{t('prices.analytics.rub')}</th>
                    <th className="py-1 pr-3 text-right font-normal">{t('prices.analytics.usd')}</th>
                    <th className="py-1 pr-3 text-right font-normal">{t('prices.analytics.last')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.chain.map((r) => (
                    <tr key={`${r.link}|${r.item_key}|${r.country}|${r.city}|${r.partner}`}
                      className="border-t border-line/50 text-foam">
                      <td className="py-1 pr-3">{t(linkKey(r.link))}</td>
                      <td className="py-1 pr-3">
                        {productKeys(r).map((k) => t(k)).join(', ')}
                        {r.group && <span className="text-mist"> · {t('prices.analytics.group')}</span>}
                      </td>
                      <td className="py-1 pr-3">
                        {countryName(r.country, lang)}
                        {r.city && <span className="text-mist"> · {t(`prices.city.${r.city}`)}</span>}
                        {r.partner && <span className="text-mist"> ← {countryName(r.partner, lang)}</span>}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums">{formatNumber(lang, r.rub_per_kg, 0)}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">
                        {r.usd_per_kg === null ? DASH : formatNumber(lang, r.usd_per_kg, 2)}
                      </td>
                      <td className="py-1 pr-3 text-right">{formatMonth(lang, r.last)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-mist">{t('prices.analytics.chainNote')}</p>
            </div>
          </section>
          {d.seasonality && (
            <section>
              <h2 className="mb-2 text-foam">{t('prices.analytics.season')}</h2>
              <div className="flex flex-wrap gap-3 text-xs tabular-nums">
                {seasonRow(d.seasonality).map((s) => (
                  <span key={s.month} className={s.pct >= 0 ? 'text-foam' : 'text-mist'}>
                    {monthName(s.month)} {s.pct > 0 ? '+' : ''}{formatNumber(lang, s.pct, 1)}%
                  </span>
                ))}
              </div>
              <p className="mt-1 text-xs text-mist">{t('prices.analytics.seasonNote')}</p>
            </section>
          )}
          {d.fx.length > 0 && (
            <section>
              <h2 className="mb-2 text-foam">{t('prices.analytics.fx')}</h2>
              <table className="border-collapse text-xs">
                <thead>
                  <tr className="text-left text-mist">
                    <th className="py-1 pr-4 font-normal">{t('prices.analytics.year')}</th>
                    <th className="py-1 pr-4 text-right font-normal">{t('prices.analytics.rubChange')}</th>
                    <th className="py-1 pr-4 text-right font-normal">{t('prices.analytics.usdChange')}</th>
                    <th className="py-1 pr-4 text-right font-normal">{t('prices.analytics.fxChange')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.fx.map((f) => (
                    <tr key={f.year} className="border-t border-line/50 text-foam tabular-nums">
                      <td className="py-1 pr-4">{f.year} / {f.vs}</td>
                      <td className="py-1 pr-4 text-right">{formatNumber(lang, f.rub_change_pct, 1)}%</td>
                      <td className="py-1 pr-4 text-right">{formatNumber(lang, f.usd_change_pct, 1)}%</td>
                      <td className="py-1 pr-4 text-right">{formatNumber(lang, f.fx_change_pct, 1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-1 text-xs text-mist">{t('prices.analytics.fxNote')}</p>
            </section>
          )}
          {d.tariffs.length > 0 && (
            <section>
              <h2 className="mb-2 text-foam">{t('prices.analytics.tariffs')}</h2>
              <table className="border-collapse text-xs">
                <thead>
                  <tr className="text-left text-mist">
                    <th className="py-1 pr-4 font-normal">{t('prices.analytics.importer')}</th>
                    <th className="py-1 pr-4 font-normal">{t('prices.export.product')}</th>
                    <th className="py-1 pr-4 font-normal">{t('prices.analytics.code')}</th>
                    <th className="py-1 pr-4 text-right font-normal">{t('prices.analytics.duty')}</th>
                    <th className="py-1 pr-4 font-normal">{t('prices.analytics.quotaRegime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.tariffs.map((x) => (
                    <tr key={`${x.importer}|${x.code}`} className="border-t border-line/50 text-foam">
                      <td className="py-1 pr-4">{countryName(x.importer, lang)}</td>
                      <td className="py-1 pr-4">{productKeys(x).map((k) => t(k)).join(', ')}</td>
                      <td className="py-1 pr-4 tabular-nums">{x.code}</td>
                      <td className="py-1 pr-4 text-right tabular-nums">
                        {x.mfn_pct === null ? x.mfn_text : `${formatNumber(lang, x.mfn_pct, 1)}%`}
                      </td>
                      <td className="py-1 pr-4">{x.import_quota ? t('prices.analytics.iq') : DASH}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-1 text-xs text-mist">{t('prices.analytics.tariffNote')}</p>
            </section>
          )}
          {d.supply.quota.length > 0 && (
            <section>
              <h2 className="mb-2 text-foam">{t('prices.analytics.supply')}</h2>
              <table className="border-collapse text-xs">
                <thead>
                  <tr className="text-left text-mist">
                    <th className="py-1 pr-4 font-normal">{t('prices.analytics.year')}</th>
                    <th className="py-1 pr-4 text-right font-normal">{t('prices.analytics.quota')}</th>
                    <th className="py-1 pr-4 text-right font-normal">{t('prices.analytics.caught')}</th>
                    <th className="py-1 pr-4 text-right font-normal">{t('prices.analytics.util')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.supply.quota.map((q) => {
                    const u = utilPct(q.quota_t, q.caught_t)
                    return (
                      <tr key={q.year} className="border-t border-line/50 text-foam tabular-nums">
                        <td className="py-1 pr-4">{q.year}</td>
                        <td className="py-1 pr-4 text-right">{formatNumber(lang, q.quota_t, 0)}</td>
                        <td className="py-1 pr-4 text-right">{formatNumber(lang, q.caught_t, 0)}</td>
                        <td className="py-1 pr-4 text-right">{u === null ? DASH : `${formatNumber(lang, u, 1)}%`}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="mt-1 text-xs text-mist">{t('prices.analytics.supplyNote')}</p>
            </section>
          )}
        </>
      )}
      {lastIdx && (
        <section>
          <h2 className="mb-2 text-foam">{t('prices.analytics.basket')}</h2>
          <p className="text-foam tabular-nums">
            {t('prices.analytics.basketLine', {
              month: formatMonth(lang, lastIdx.month), index: formatNumber(lang, lastIdx.index, 1) })}
          </p>
          <p className="mt-1 text-xs text-mist">{t('prices.analytics.basketNote')}</p>
        </section>
      )}
    </div>
  )
}
